import React from 'react';

const WEEKDAY_LABELS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

function formatCompactNumber(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '--';
  if (numeric >= 1000) return `${(numeric / 1000).toFixed(1)}k`;
  return String(numeric);
}

function formatScore(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? `${numeric.toFixed(1)}%` : '--';
}

function formatDateTime(value) {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatShortDate(value) {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function MetricCard({ label, value, helper, tone = 'brand' }) {
  return (
    <article className={`dashboard-analytics__metric dashboard-analytics__metric--${tone}`}>
      <span className="dashboard-analytics__metric-label">{label}</span>
      <strong className="dashboard-analytics__metric-value">{value}</strong>
      <span className="dashboard-analytics__metric-helper">{helper}</span>
    </article>
  );
}

function ExamSummaryCard({ examHistory, examCards }) {
  const latestExam = examCards?.[0] || null;

  return (
    <article className="dashboard-analytics__feature-card dashboard-analytics__feature-card--exam">
      <div className="dashboard-analytics__feature-head">
        <div>
          <span className="dashboard-analytics__feature-kicker">Progress</span>
          <h3>Your quiz progress</h3>
        </div>
        <div className="dashboard-analytics__score-pill">
          {formatScore(examHistory?.averageScorePct)}
        </div>
      </div>
      <div className="dashboard-analytics__exam-snapshot">
        <div>
          <strong>{formatScore(examHistory?.bestScorePct)}</strong>
          <span>best score</span>
        </div>
        <div>
          <strong>{formatScore(examHistory?.latestScorePct)}</strong>
          <span>latest score</span>
        </div>
        <div>
          <strong>{formatCompactNumber(examHistory?.totalAttempts || 0)}</strong>
          <span>attempts</span>
        </div>
      </div>
      {latestExam ? (
        <div className="dashboard-analytics__exam-callout">
          <strong>{latestExam.title || latestExam.courseId}</strong>
          <span>
            {formatScore(latestExam.avgScorePct)} course avg ·{' '}
            {formatShortDate(latestExam.latestAt)}
          </span>
        </div>
      ) : (
        <div className="dashboard-analytics__empty-mini">No exam results yet.</div>
      )}
    </article>
  );
}

function RankedCards({ title, subtitle, items, renderMeta, emptyLabel, tone = 'blue' }) {
  return (
    <article
      className={`dashboard-analytics__panel dashboard-analytics__panel--cardlist dashboard-analytics__panel--${tone}`}
    >
      <div className="dashboard-analytics__panel-head">
        <h3>{title}</h3>
        <span>{subtitle}</span>
      </div>
      {items.length === 0 ? (
        <div className="dashboard-analytics__empty-mini">{emptyLabel}</div>
      ) : (
        <div className="dashboard-analytics__cardlist">
          {items.map((item, index) => (
            <div
              key={item.courseId || item.taskId || item.examId || `${title}-${index}`}
              className="dashboard-analytics__rank-card"
            >
              <div className="dashboard-analytics__rank-card-head">
                <span className="dashboard-analytics__rank-index">#{index + 1}</span>
                <strong>{item.title || item.examId || item.courseId || item.taskId}</strong>
              </div>
              <span>{renderMeta(item)}</span>
            </div>
          ))}
        </div>
      )}
    </article>
  );
}

function DailyBars({ series }) {
  const maxValue = Math.max(...series.map((item) => item.events), 1);

  return (
    <div className="dashboard-analytics__bars">
      {series.map((item) => (
        <div
          key={item.date}
          className="dashboard-analytics__bar-group"
          title={`${item.date}: ${item.events} learning moments`}
        >
          <div
            className="dashboard-analytics__bar"
            style={{ height: `${Math.max(10, (item.events / maxValue) * 100)}%` }}
          />
          <span className="dashboard-analytics__bar-label">
            {new Date(item.date).toLocaleDateString([], { month: 'numeric', day: 'numeric' })}
          </span>
        </div>
      ))}
    </div>
  );
}

function ActivityHeatmap({ matrix }) {
  const maxValue = Math.max(...matrix.flat().map((cell) => cell.count), 1);

  return (
    <div className="dashboard-analytics__hour-heatmap">
      {matrix.map((row, rowIndex) => (
        <div key={`hour-row-${rowIndex}`} className="dashboard-analytics__hour-row">
          <span className="dashboard-analytics__hour-label">{WEEKDAY_LABELS[rowIndex]}</span>
          <div className="dashboard-analytics__hour-cells">
            {row.map((cell) => (
              <span
                key={`${cell.day}-${cell.hour}`}
                className="dashboard-analytics__hour-cell"
                title={`${cell.hour}:00 · ${cell.count} learning moments`}
                style={{ opacity: Math.max(0.1, cell.count / maxValue) }}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function ExamPerformanceList({ items }) {
  return (
    <div className="dashboard-analytics__table">
      {items.length === 0 ? (
        <div className="dashboard-analytics__empty-mini">No exam summaries yet.</div>
      ) : (
        items.map((item) => (
          <div key={item.courseId || item.title} className="dashboard-analytics__table-row">
            <div>
              <strong>{item.title || item.courseId}</strong>
              <span>{item.attempts} attempts</span>
            </div>
            <div>
              <strong>{formatScore(item.avgScorePct)}</strong>
              <span>avg · {formatScore(item.bestScorePct)} best</span>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function TopicSignals({ items }) {
  return (
    <div className="dashboard-analytics__tags">
      {items.map((item) => (
        <div key={item.topic} className="dashboard-analytics__tag-card">
          <strong>{item.topic}</strong>
          <span>
            {item.count} revisits · {item.chats} chats · {item.exams} quizzes · {item.scenes}{' '}
            lessons
          </span>
        </div>
      ))}
    </div>
  );
}

function RecentActivity({ items }) {
  return (
    <div className="dashboard-analytics__timeline">
      {items.map((item) => (
        <div
          key={item.id || `${item.type}-${item.occurredAt}`}
          className="dashboard-analytics__timeline-row"
        >
          <span
            className={`dashboard-analytics__timeline-dot ${
              item.success
                ? 'dashboard-analytics__timeline-dot--ok'
                : 'dashboard-analytics__timeline-dot--bad'
            }`}
          />
          <div>
            <strong>{item.type.replace(/_/g, ' ')}</strong>
            <span>
              {formatDateTime(item.occurredAt)}
              {item.scorePct != null ? ` · ${formatScore(item.scorePct)}` : ''}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

const EventAnalyticsPanel = ({ analytics, days, onDaysChange, loading, error }) => {
  const summary = analytics?.summary || {};
  const mostActiveCourses = analytics?.mostActiveCourses || [];
  const finishedTasks = analytics?.finishedTasks || [];
  const examPerformanceCards = analytics?.examPerformanceCards || [];
  const familyBreakdown = analytics?.familyBreakdown || [];
  const dailySeries = analytics?.dailySeries || [];
  const heatmap = analytics?.heatmap || [];
  const examHistory = analytics?.examHistory || { events: [], totalAttempts: 0 };
  const topicSignals = analytics?.topicSignals || [];
  const recentTimeline = analytics?.recentTimeline || [];

  return (
    <section className="dashboard-analytics">
      <div className="dashboard-analytics__header">
        <div>
          <span className="dashboard-analytics__eyebrow">Progress Overview</span>
          <h2 className="dashboard-analytics__title">Your progress at a glance</h2>
          <p className="dashboard-analytics__subtitle">
            A friendly snapshot of your study rhythm, course momentum, finished work, and quiz
            progress.
          </p>
        </div>
        <div
          className="dashboard-analytics__filters"
          role="group"
          aria-label="Analytics date window"
        >
          {[7, 30, 90].map((value) => (
            <button
              key={value}
              type="button"
              className={value === days ? 'is-active' : ''}
              onClick={() => onDaysChange(value)}
            >
              {value}d
            </button>
          ))}
        </div>
      </div>

      {error ? (
        <div className="dashboard-analytics__state dashboard-analytics__state--error">{error}</div>
      ) : null}
      {loading ? <div className="dashboard-analytics__state">Loading analytics…</div> : null}

      <div className="dashboard-analytics__metrics">
        <MetricCard
          label="Learning Moments"
          value={formatCompactNumber(summary.totalEvents)}
          helper={`${summary.activeDays || 0} active days this period`}
        />
        <MetricCard
          label="Average Quiz Score"
          value={formatScore(summary.avgExamScore)}
          helper={`${summary.quizAttemptCount || 0} completed attempts`}
          tone="success"
        />
        <MetricCard
          label="Study Time"
          value={`${formatCompactNumber(summary.studyMinutes || 0)} min`}
          helper={`${summary.activeCourseCount || 0} active courses`}
          tone="warm"
        />
        <MetricCard
          label="Moments To Revisit"
          value={formatCompactNumber(summary.failedEvents || 0)}
          helper="A few moments that may deserve another look"
          tone="danger"
        />
      </div>

      <div className="dashboard-analytics__hero-grid">
        <ExamSummaryCard examHistory={examHistory} examCards={examPerformanceCards} />
      </div>

      <div className="dashboard-analytics__grid">
        <RankedCards
          title="Most Active Courses"
          subtitle="Where your attention has been going"
          items={mostActiveCourses}
          renderMeta={(item) =>
            `${item.events} learning moments · ${item.chats} chats · ${item.exams} quizzes`
          }
          emptyLabel="No course activity yet."
          tone="blue"
        />

        <RankedCards
          title="Frequently Finished Tasks"
          subtitle="Small wins that keep adding up"
          items={finishedTasks}
          renderMeta={(item) =>
            `${item.completions} completions · latest ${formatShortDate(item.latestAt)}`
          }
          emptyLabel="No completed tasks yet."
          tone="green"
        />

        <article className="dashboard-analytics__panel dashboard-analytics__panel--wide">
          <div className="dashboard-analytics__panel-head">
            <h3>Your study rhythm</h3>
            <span>Last {days} days</span>
          </div>
          <DailyBars series={dailySeries} />
        </article>

        <article className="dashboard-analytics__panel dashboard-analytics__panel--wide">
          <div className="dashboard-analytics__panel-head">
            <h3>When you learn most</h3>
            <span>Darker squares mean busier study hours</span>
          </div>
          <ActivityHeatmap matrix={heatmap} />
        </article>

        <article className="dashboard-analytics__panel dashboard-analytics__panel--amber">
          <div className="dashboard-analytics__panel-head">
            <h3>Quiz progress</h3>
            <span>Your recent results</span>
          </div>
          <ExamPerformanceList items={examPerformanceCards} />
        </article>

        <article className="dashboard-analytics__panel dashboard-analytics__panel--purple">
          <div className="dashboard-analytics__panel-head">
            <h3>Focus topics</h3>
            <span>Topics you come back to most often</span>
          </div>
          <TopicSignals items={topicSignals} />
        </article>

        <article className="dashboard-analytics__panel">
          <div className="dashboard-analytics__panel-head">
            <h3>Your learning mix</h3>
            <span>How your time has been spread across learning moments</span>
          </div>
          <div className="dashboard-analytics__pill-grid">
            {familyBreakdown.slice(0, 6).map((item) => (
              <div key={item.family} className="dashboard-analytics__family-pill">
                <strong>{item.family.replace(/_/g, ' ')}</strong>
                <span>{item.count} learning moments</span>
              </div>
            ))}
          </div>
        </article>

        <article className="dashboard-analytics__panel dashboard-analytics__panel--wide">
          <div className="dashboard-analytics__panel-head">
            <h3>Recent activity</h3>
            <span>Your latest learning moments, all in one place</span>
          </div>
          <RecentActivity items={recentTimeline} />
        </article>
      </div>
    </section>
  );
};

export default EventAnalyticsPanel;
